import React from "react";
import { FileText, List, PenLine, Tag } from "lucide-react";
import { ToolCardShell, type CardStatus } from "../tool";
import { ToolInput, ToolOutput } from "../tool";
import type { ToolCardComponentProps } from "../tool-registry";
import { ToolCardDiff } from "./ToolCardDiff";

const CHAPTER_TOOL_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; iconColor: string; defaultExpanded: boolean }
> = {
  chapter_read: {
    label: "读取章节",
    icon: <FileText className="h-4 w-4" />,
    iconColor: "text-blue-500",
    defaultExpanded: false,
  },
  chapter_list: {
    label: "章节列表",
    icon: <List className="h-4 w-4" />,
    iconColor: "text-blue-500",
    defaultExpanded: false,
  },
  chapter_write: {
    label: "生成章节",
    icon: <PenLine className="h-4 w-4" />,
    iconColor: "text-amber-500",
    defaultExpanded: true,
  },
  chapter_update: {
    label: "编辑章节",
    icon: <PenLine className="h-4 w-4" />,
    iconColor: "text-orange-500",
    defaultExpanded: true,
  },
  title_rename: {
    label: "批量重命名",
    icon: <Tag className="h-4 w-4" />,
    iconColor: "text-violet-500",
    defaultExpanded: false,
  },
};

const WRITE_MODE_LABELS: Record<string, string> = {
  write: "写入",
  append: "追加",
};

function formatCharCount(n: number): string {
  return `约 ${n.toLocaleString()} 字`;
}

function getResultCharCount(result: unknown): number {
  if (typeof result === "string") return result.length;
  if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (typeof r.content === "string") return r.content.length;
    if (typeof r.text === "string") return r.text.length;
  }
  return 0;
}

const ChapterReadSummary: React.FC<{ args: Record<string, unknown>; charCount: number }> = ({
  args,
  charCount,
}) => {
  const num = args.num as number | undefined;
  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {num != null && <span>第 {num} 章</span>}
      {charCount > 0 && <span>{formatCharCount(charCount)}</span>}
    </div>
  );
};

const ChapterListSummary: React.FC<{ args: Record<string, unknown>; result: unknown }> = ({
  args,
  result,
}) => {
  let total = 0;
  if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (Array.isArray(r.chapters)) total = r.chapters.length;
    else if (Array.isArray(r.items)) total = r.items.length;
    else if (typeof r.total === "number") total = r.total;
    else if (typeof r.count === "number") total = r.count;
  }
  const filterNum = args.num as number | undefined;
  const filterName = args.name as string | undefined;

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {total > 0 && <span>共 {total} 章</span>}
      {filterNum != null && <span>筛选: 第{filterNum}章</span>}
      {filterName && <span>筛选: {filterName}</span>}
    </div>
  );
};

const ChapterWriteSummary: React.FC<{
  args: Record<string, unknown>;
  status: CardStatus;
  charCount: number;
}> = ({ args, status, charCount }) => {
  const num = args.num as number | undefined;
  const mode = String(args.mode || "write");
  const modeLabel = WRITE_MODE_LABELS[mode] || mode;

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {num != null && <span>第 {num} 章</span>}
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">
        {modeLabel}
      </span>
      {status === "executing" && (
        <span className="text-amber-600 animate-pulse">正在创作...</span>
      )}
      {status === "success" && charCount > 0 && (
        <span>{formatCharCount(charCount)}</span>
      )}
    </div>
  );
};

const ChapterUpdateSummary: React.FC<{ args: Record<string, unknown> }> = ({ args }) => {
  const num = args.num as number | undefined;
  const oldText = typeof args.old_text === "string" ? args.old_text : "";
  const newText = typeof args.new_text === "string" ? args.new_text : "";
  const useRegex = args.use_regex as boolean | undefined;

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {num != null && <span>第 {num} 章</span>}
      {(oldText.length > 0 || newText.length > 0) && (
        <ToolCardDiff additions={newText.length} deletions={oldText.length} />
      )}
      {useRegex && (
        <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-orange-700">
          正则替换
        </span>
      )}
    </div>
  );
};

const TitleRenameSummary: React.FC<{ args: Record<string, unknown>; result: unknown }> = ({
  args,
  result,
}) => {
  const renameType = args.type as string | undefined;
  const typeLabel = renameType === "setting" ? "设定名称" : "章节标题";

  const names = args.names as Array<{ old: string; new: string }> | undefined;
  const renameItems = Array.isArray(names) ? names : [];

  let resultCount = 0;
  if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (typeof r.renamed === "number") resultCount = r.renamed;
    else if (typeof r.count === "number") resultCount = r.count;
  }

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-violet-700">
        {typeLabel}
      </span>
      {(renameItems.length > 0 || resultCount > 0) && (
        <span>重命名 {renameItems.length || resultCount} 项</span>
      )}
      {renameItems.length > 0 && (
        <span className="truncate max-w-[200px]">
          {renameItems[0].old} → {renameItems[0].new}
        </span>
      )}
    </div>
  );
};

export const ChapterCard: React.FC<ToolCardComponentProps> = ({ toolCall }) => {
  const cardStatus: CardStatus = toolCall.status || "detected";
  const toolName = toolCall.toolName;
  const args = toolCall.args || {};
  const result = toolCall.result;
  const config = CHAPTER_TOOL_CONFIG[toolName] || CHAPTER_TOOL_CONFIG.chapter_read;
  const charCount = result ? getResultCharCount(result) : 0;

  const renderSummary = () => {
    switch (toolName) {
      case "chapter_read":
        return <ChapterReadSummary args={args} charCount={charCount} />;
      case "chapter_list":
        return <ChapterListSummary args={args} result={result} />;
      case "chapter_write":
        return <ChapterWriteSummary args={args} status={cardStatus} charCount={charCount} />;
      case "chapter_update":
        return <ChapterUpdateSummary args={args} />;
      case "title_rename":
        return <TitleRenameSummary args={args} result={result} />;
      default:
        return null;
    }
  };

  return (
    <ToolCardShell
      toolName={config.label}
      status={cardStatus}
      icon={config.icon}
      iconColor={config.iconColor}
      defaultExpanded={config.defaultExpanded}
    >
      {renderSummary()}
      {toolCall.args && Object.keys(toolCall.args).length > 0 && (
        <ToolInput data={toolCall.args} />
      )}
      {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
        <ToolOutput data={toolCall.result} error={toolCall.error} />
      ) : null}
    </ToolCardShell>
  );
};
